import { Button } from "@/components/ui/button";
import { STATUS_LABEL, STATUS_ORDER } from "@/lib/roMeta";
import { toast } from "sonner";
import { FileBarChart, Download, ListChecks, FileText } from "lucide-react";

const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

function counts(records) {
  const by = Object.fromEntries(STATUS_ORDER.map((s) => [s, 0]));
  records.forEach((r) => { if (by[r.compliance_status] != null) by[r.compliance_status] += 1; });
  return by;
}
const opsOf = (r) => (Array.isArray(r.labor_ops) && r.labor_ops.length ? r.labor_ops.join(", ") : r.labor_op_code || "Not detected");
const packsOf = (r) => (r.packs || []).map((p) => p.label || p).join(", ") || "General";
const findingsOf = (r) => (r.findings_detail?.length ? r.findings_detail : (r.flags || []).map((t) => ({ title: t })));

export function BatchSummary({ records = [], dealership, adminName, logoUrl }) {
  const meta = `${esc(dealership || "Dealership")} • ${esc(adminName || "Warranty Administrator")}`;
  const gen = new Date().toLocaleString();

  const cardsHtml = (items) => {
    const by = counts(items);
    return [["ROs in Batch", items.length], ...STATUS_ORDER.map((s) => [STATUS_LABEL[s], by[s]])]
      .map(([l, v]) => `<div style="border:1px solid #e2e8f0;border-radius:10px;padding:12px 16px;min-width:150px">
        <div style="font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:#64748b">${esc(l)}</div>
        <div style="font-size:30px;font-weight:800;color:#0f172a">${v}</div></div>`).join("");
  };

  const tableHtml = (items) => {
    const rows = items.map((r) => {
      const f = findingsOf(r);
      const primary = f[0]?.title || (r.compliance_status === "skip_maintenance" ? "Maintenance — no warranty review required." : "No issues detected.");
      return `<tr>
        <td style="padding:7px 10px;border:1px solid #e2e8f0;font-weight:700">${esc(r.ro_number || "—")}</td>
        <td style="padding:7px 10px;border:1px solid #e2e8f0;color:#475569">${esc(r.source_filename || "")}</td>
        <td style="padding:7px 10px;border:1px solid #e2e8f0">${esc(STATUS_LABEL[r.compliance_status] || r.compliance_status)}</td>
        <td style="padding:7px 10px;border:1px solid #e2e8f0;text-align:center;font-weight:700">${r.score == null ? "—" : esc(r.score)}</td>
        <td style="padding:7px 10px;border:1px solid #e2e8f0;color:#334155">${esc(primary)}</td></tr>`;
    }).join("");
    return `<table style="border-collapse:collapse;width:100%;font-size:12px">
      <thead><tr style="background:#f1f5f9">
        <th style="padding:8px 10px;border:1px solid #e2e8f0;text-align:left">Repair Order #</th>
        <th style="padding:8px 10px;border:1px solid #e2e8f0;text-align:left">Source File</th>
        <th style="padding:8px 10px;border:1px solid #e2e8f0;text-align:left">Status</th>
        <th style="padding:8px 10px;border:1px solid #e2e8f0;text-align:center">Score</th>
        <th style="padding:8px 10px;border:1px solid #e2e8f0;text-align:left">Primary Finding</th>
      </tr></thead><tbody>${rows}</tbody></table>`;
  };

  const detailHtml = (r) => {
    const f = findingsOf(r);
    const findings = f.length
      ? f.map((x) => `<div style="border-left:4px solid #a00;padding:8px 10px;margin:8px 0"><b>${esc(x.title)}</b>${x.action ? `<div style="margin-top:3px">${esc(x.action)}</div>` : ""}${x.rule ? `<div style="color:#777;font-size:11px;margin-top:3px">Rule: ${esc(x.rule)}</div>` : ""}</div>`).join("")
      : `<div style="border-left:4px solid #176b42;padding:8px 10px;margin:8px 0"><b>${r.compliance_status === "skip_maintenance" ? "Maintenance — no warranty review required." : "No automated compliance deficiencies identified."}</b></div>`;
    return `<section style="border-top:3px solid #222;padding-top:16px;margin-top:18px;page-break-after:always">
      <h2 style="margin:0 0 8px">RO ${esc(r.ro_number || "Unknown")}</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px 18px;margin-bottom:12px;font-size:13px">
        <div><b>Vehicle:</b> ${esc([r.vehicle_year, r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ") || "Not detected")}</div>
        <div><b>VIN:</b> ${esc(r.vin || "Not detected")}</div>
        <div><b>Status:</b> ${esc(STATUS_LABEL[r.compliance_status] || r.compliance_status)}</div>
        <div><b>Score:</b> ${r.score == null ? "—" : esc(r.score)}</div>
        <div><b>Labor Ops:</b> ${esc(opsOf(r))}</div>
        <div><b>Rule Packs:</b> ${esc(packsOf(r))}</div>
        <div><b>Source:</b> ${esc(r.source_filename || "")}</div>
      </div>
      <h3 style="margin:12px 0 4px">Compliance Findings</h3>${findings}
      <h3 style="margin:12px 0 4px">Customer Concern</h3><div style="white-space:pre-wrap;border:1px solid #ccc;padding:8px;margin-bottom:8px">${esc(r.complaint || "Not extracted")}</div>
      <h3 style="margin:8px 0 4px">Cause</h3><div style="white-space:pre-wrap;border:1px solid #ccc;padding:8px;margin-bottom:8px">${esc(r.cause || "Not extracted")}</div>
      <h3 style="margin:8px 0 4px">Correction</h3><div style="white-space:pre-wrap;border:1px solid #ccc;padding:8px">${esc(r.correction || "Not extracted")}</div>
    </section>`;
  };

  const openReport = (title, subtitle, bodyHtml) => {
    const win = window.open("", "_blank", "width=1000,height=760");
    if (!win) { toast.error("Please allow pop-ups for this site to open the printable report."); return; }
    win.document.write(`<html><head><title>${esc(title)}</title></head>
      <body style="font-family:Arial,Helvetica,sans-serif;padding:30px;color:#0f172a">
      <div style="display:flex;align-items:center;gap:16px;border-bottom:2px solid #e2e8f0;padding-bottom:14px;margin-bottom:16px">
        ${logoUrl ? `<img src="${esc(logoUrl)}" alt="logo" style="max-height:52px;max-width:180px;object-fit:contain" onerror="this.style.display='none'"/>` : ""}
        <div>
          <h1 style="margin:0;font-size:24px">${esc(title)}</h1>
          <p style="color:#64748b;margin:4px 0 2px;font-size:13px">${meta}</p>
          <p style="color:#94a3b8;margin:0;font-size:12px">${esc(subtitle)}</p>
        </div>
      </div>
      ${bodyHtml}
      <p style="color:#94a3b8;margin-top:22px;font-size:11px">Warranty Compliance Pro — internal compliance review. Final claim submission remains the responsibility of the Warranty Administrator.</p>
      </body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const printSummary = () =>
    openReport("Warranty Compliance Pro — Current Batch Summary",
      `Generated ${gen} from all ${records.length} currently loaded repair-order records, regardless of search or status filters.`,
      `<div style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:22px">${cardsHtml(records)}</div>${tableHtml(records)}`);

  const printFindings = () => {
    const items = records.filter((r) => (r.flags?.length || r.findings_detail?.length) || ["review", "required"].includes(r.compliance_status));
    if (!items.length) return openReport("Warranty Compliance Pro — Findings Only", "No repair orders currently have findings.", `<div style="display:flex;flex-wrap:wrap;gap:12px">${cardsHtml(records)}</div>`);
    openReport("Warranty Compliance Pro — Findings Only",
      "Only repair orders with automated findings or Review/Required status.",
      `<div style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:6px">${cardsHtml(items)}</div>${items.map(detailHtml).join("")}`);
  };

  const printFull = () =>
    openReport("Warranty Compliance Pro — Full Audit Report",
      "Complete batch including Ready, Review, Required, Pending Verification, and Maintenance.",
      `<div style="display:flex;flex-wrap:wrap;gap:12px;margin-bottom:6px">${cardsHtml(records)}</div>${records.map(detailHtml).join("")}`);

  const csv = () => {
    const header = ["Repair Order #", "VIN", "Status", "Score", "Labor Ops", "Rule Packs", "Source File", "Findings"];
    const lines = [header.map(csvCell).join(",")];
    records.forEach((r) => lines.push([
      r.ro_number, r.vin, STATUS_LABEL[r.compliance_status] || r.compliance_status, r.score,
      opsOf(r), packsOf(r), r.source_filename,
      findingsOf(r).map((x) => x.title + (x.action ? " — " + x.action : "")).join(" | "),
    ].map(csvCell).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `batch-audit-summary-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="outline" size="lg" onClick={printSummary} disabled={!records.length} data-testid="batch-summary-print">
        <FileBarChart className="h-4 w-4" /> Batch Summary
      </Button>
      <Button variant="outline" size="lg" onClick={printFindings} disabled={!records.length} data-testid="report-findings-only">
        <ListChecks className="h-4 w-4" /> Findings Only
      </Button>
      <Button variant="outline" size="lg" onClick={printFull} disabled={!records.length} data-testid="report-full-audit">
        <FileText className="h-4 w-4" /> Full Report
      </Button>
      <Button variant="ghost" size="lg" onClick={csv} disabled={!records.length} data-testid="batch-summary-csv">
        <Download className="h-4 w-4" /> CSV
      </Button>
    </div>
  );
}
